// fullscreen-zoom.js - 全屏图片缩放动画工具库

/**
 使用例子
 提前需要加载GSAP库
  <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js"></script>
  <script src="https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js"></script>
 
 html部分
  <!-- 固定屏幕区域 -->
  <section class="fsz-pinned-screen" id="pinnedScreen">
      <!-- 图片容器 -->
      <div class="fsz-media-container">
          <img src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4" 
               alt="风景" 
               class="fsz-fullscreen-media" 
               id="mainMedia">
      </div>

      <!-- 屏幕内容 -->
      <div class="screen-content">
          <h1>全屏图片缩放动画</h1>
          <p>向下滚动，全屏图片将在当前屏幕内缩放到目标区域。</p>
      </div>

      <!-- 目标区域 -->
      <div class="fsz-target-container" id="targetArea">
          <span>图片最终位置</span>
      </div>
  </section>
 
 js部分
 
 // 初始化全屏缩放动画
 const fsz = new FullScreenZoom({
     mediaSelector: '#mainMedia',
     targetSelector: '#targetArea',
     screenSelector: '#pinnedScreen',
     scrollDistance: 2000,
     scrubDelay: 1.5,
     autoInit: true,
     syncTargetAspectToImage: true,
     onProgressChange: (progress) => console.log('进度:', progress),
     onAnimationStart: () => console.log('动画开始'),
     onAnimationComplete: () => console.log('动画完成'),
     // 在默认动画序列之后，向同一条滚动驱动的时间线追加自定义动画
     extendTimeline: (tl, el, api) => {
         // tl: GSAP Timeline（主时间线）
         // el: 常用元素引用 { image, target, screen }
         // api: 常用计算工具 { calculateTargetScale, calculateTargetPosition }
         tl.to(el.image, { borderRadius: '24px', duration: 0.5, ease: 'back.out(1.7)' }, 0.8);
     }
 });
// 动态更新配置
// zoom.recalculate();
 
 // 也可以在实例化后获取主时间线，继续追加动画（同样受滚动进度驱动）
 const tl = fsz.getTimeline();
 const { image } = fsz.getElements();
 tl && tl.to(image, { boxShadow: '0 25px 70px rgba(0,0,0,0.7)', duration: 0.6 }, 1.2);
 
 // 若希望在缩放动画完成后播放一次性的补充动画（不受滚动驱动）
 // 可通过 onAnimationComplete 触发独立的 gsap 动画
 // onAnimationComplete: () => {
 //   gsap.fromTo('#someElement', { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6 });
 // }
*/

class FullScreenZoom {
    /**
     * 初始化全屏缩放动画
     * @param {Object} options 配置选项
     * @param {string} options.mediaSelector 媒体选择器（img 或 video）
     * @param {string} options.targetSelector 目标区域选择器
     * @param {string} options.screenSelector 屏幕容器选择器
     * @param {number} options.scrollDistance 动画滚动距离（默认2000px）
     * @param {number} options.scrubDelay 动画延迟（默认1.5）
     * @param {boolean} options.autoInit 是否自动初始化（默认true）
     * @param {boolean} options.hasProgressBar 是否有进度条（默认false）
     * @param {Function} options.onProgressChange 进度变化回调
     * @param {Function} options.onAnimationStart 动画开始回调
     * @param {Function} options.onAnimationComplete 动画完成回调
     * @param {Function} options.extendTimeline 在默认序列之后扩展主时间线的回调（滚动驱动）
     * @param {number} options.zoomDuration 主缩放/位移动画时长（默认0.8）
     * @param {string} options.zoomEase 主缩放/位移动画缓动（默认"power2.out"）
     * @param {number} options.targetFadeInDuration 目标区域淡入时长（默认0.8）
     * @param {string} options.targetFadeInEase 目标区域淡入缓动（默认"power2.out"）
     * @param {number} options.targetFadeOutDuration 回滚时目标区域淡出时长（默认0.3）
     * @param {string} options.targetFadeOutEase 回滚时目标区域淡出缓动（默认"power2.out"）
     * @param {Function} options.onZoomComplete 主缩放位移完成回调
     */
    constructor(options = {}) {
        // 默认配置
        this.config = {
            mediaSelector: '.fullscreen-image',
            targetSelector: '.target-container',
            screenSelector: '.pinned-screen',
            scrollDistance: 2000,
            scrubDelay: 1.5,
            autoInit: true,
            syncTargetAspectToImage: true,
            onProgressChange: null,
            onAnimationStart: null,
            onAnimationComplete: null,
            extendTimeline: null,
            zoomDuration: 0.8,
            zoomEase: 'power2.out',
            targetFadeInDuration: 0.8,
            targetFadeInEase: 'power2.out',
            targetFadeOutDuration: 0.3,
            targetFadeOutEase: 'power2.out',
            onZoomComplete: null,
            ...options
        };
        if (options.imageSelector && !options.mediaSelector) {
            this.config.mediaSelector = options.imageSelector;
        }

        // 内部状态
        this.isInitialized = false;
        this.isAnimating = false;
        this.currentProgress = 0;
        this.mainTimeline = null;
        this.scrollTrigger = null;
        this.imgNaturalWidth = 0;
        this.imgNaturalHeight = 0;
        this.imgAspectRatio = 0;

        // DOM元素引用
        this.mainMedia = null;
        this.targetArea = null;
        this.pinnedScreen = null;
        this.screenTitle = null;
        this.screenDescription = null;
        this.progressBar = null;
        this.ownsProgressBar = false;
        this.progressIndicator = null;

        // 初始化
        if (this.config.autoInit) {
            this.init();
        }
    }

    /**
     * 初始化工具库
     */
    init() {
        if (this.isInitialized) return;

        // 注册GSAP插件
        if (typeof gsap === 'undefined') {
            console.error('GSAP is required. Please include GSAP library.');
            return;
        }
        
        if (typeof ScrollTrigger === 'undefined') {
            gsap.registerPlugin(ScrollTrigger);
        }

        // 获取DOM元素
        this.mainMedia = document.querySelector(this.config.mediaSelector);
        this.targetArea = document.querySelector(this.config.targetSelector);
        this.pinnedScreen = document.querySelector(this.config.screenSelector);

        // 检查必要元素
        if (!this.mainMedia || !this.targetArea || !this.pinnedScreen) {
            console.error('Required elements not found:', {
                image: !!this.mainMedia,
                target: !!this.targetArea,
                screen: !!this.pinnedScreen
            });
            return;
        }

        // 创建进度条
        if(this.config.hasProgressBar) {
            this.createProgressBar();
        }

        // 等待图片加载
        this.waitForImageLoad();

        
    //   if (document.readyState === 'complete') this.waitForImageLoad()();
    //   else window.addEventListener('load', this.waitForImageLoad());
    }

    /**
     * 等待图片加载完成
     */
    waitForImageLoad() {
        const isVideo = this.mainMedia && this.mainMedia.tagName && this.mainMedia.tagName.toLowerCase() === 'video';
        const checkAndInit = () => {
            if (!isVideo) {
                if (this.mainMedia.complete && this.mainMedia.naturalWidth > 0) {
                    this.setupImageProperties();
                    this.applyTargetAspectRatio();
                    this.createAnimation();
                    this.isInitialized = true;
                } else {
                    setTimeout(() => {
                        if (!this.isInitialized) {
                            if (this.mainMedia.naturalWidth > 0) {
                                this.setupImageProperties();
                                this.applyTargetAspectRatio();
                                this.createAnimation();
                                this.isInitialized = true;
                            } else {
                                this.setupDefaultImageProperties();
                                this.applyTargetAspectRatio();
                                this.createAnimation();
                                this.isInitialized = true;
                            }
                        }
                    }, 3000);
                }
            } else {
                const vw = this.mainMedia.videoWidth || 0;
                const vh = this.mainMedia.videoHeight || 0;
                if (vw > 0 && vh > 0) {
                    this.setupImageProperties();
                    this.applyTargetAspectRatio();
                    this.createAnimation();
                    this.isInitialized = true;
                } else {
                    setTimeout(() => {
                        if (!this.isInitialized) {
                            const vw2 = this.mainMedia.videoWidth || 0;
                            const vh2 = this.mainMedia.videoHeight || 0;
                            if (vw2 > 0 && vh2 > 0) {
                                this.setupImageProperties();
                                this.applyTargetAspectRatio();
                                this.createAnimation();
                                this.isInitialized = true;
                            } else {
                                this.setupDefaultImageProperties();
                                this.applyTargetAspectRatio();
                                this.createAnimation();
                                this.isInitialized = true;
                            }
                        }
                    }, 3000);
                }
            }
        };

        if (!isVideo) {
            if (this.mainMedia.complete) {
                checkAndInit();
            } else {
                this.mainMedia.addEventListener('load', checkAndInit, { once: true });
            }
        } else {
            if ((this.mainMedia.readyState || 0) >= 1 && this.mainMedia.videoWidth > 0) {
                checkAndInit();
            } else {
                this.mainMedia.addEventListener('loadedmetadata', checkAndInit, { once: true });
            }
        }
    }

    /**
     * 设置图片属性
     */
    setupImageProperties() {
        const isVideo = this.mainMedia && this.mainMedia.tagName && this.mainMedia.tagName.toLowerCase() === 'video';
        if (isVideo) {
            this.imgNaturalWidth = this.mainMedia.videoWidth || 0;
            this.imgNaturalHeight = this.mainMedia.videoHeight || 0;
        } else {
            this.imgNaturalWidth = this.mainMedia.naturalWidth;
            this.imgNaturalHeight = this.mainMedia.naturalHeight;
        }
        this.imgAspectRatio = this.imgNaturalWidth / this.imgNaturalHeight;
    }

    /**
     * 设置默认图片属性（图片加载失败时使用）
     */
    setupDefaultImageProperties() {
        this.imgNaturalWidth = 1600;
        this.imgNaturalHeight = 900;
        this.imgAspectRatio = 1600 / 900;
    }

    /**
     * 应用目标区域的纵横比
     */
    applyTargetAspectRatio() {
        if (!this.config.syncTargetAspectToImage) return;
        if (!this.targetArea) return;
        if (this.imgNaturalWidth > 0 && this.imgNaturalHeight > 0) {
            this.targetArea.style.aspectRatio = `${this.imgNaturalWidth} / ${this.imgNaturalHeight}`;
        }
    }

    /**
     * 创建进度条
     */
    createProgressBar() {
        const existingIndicator = this.pinnedScreen.querySelector('.fsz-progress-indicator');
        if (existingIndicator) {
            this.progressIndicator = existingIndicator;
            const existingBar = existingIndicator.querySelector('.fsz-progress-bar');
            if (existingBar) {
                this.progressBar = existingBar;
            }
            this.ownsProgressBar = false;
            return;
        }

        this.progressIndicator = document.createElement('div');
        this.progressIndicator.className = 'fsz-progress-indicator';
        this.progressIndicator.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 5px;
            background: rgba(255, 255, 255, 0.1);
            z-index: 1000;
        `;

        this.progressBar = document.createElement('div');
        this.progressBar.className = 'fsz-progress-bar';
        this.progressBar.style.cssText = `
            display: block;
            height: 100%;
            background: linear-gradient(90deg, #3b82f6, #8b5cf6);
            width: 0%;
            transition: width 0.1s linear;
        `;

        this.progressIndicator.appendChild(this.progressBar);
        this.pinnedScreen.appendChild(this.progressIndicator);
        this.ownsProgressBar = true;
    }

    /**
     * 计算初始缩放比例
     * @returns {number} 缩放比例
     */
    calculateInitialScale() {
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const screenAspectRatio = screenWidth / screenHeight;

        if (this.imgAspectRatio > screenAspectRatio) {
            // 图片比屏幕宽，以高度为基准，宽度会超出
            return screenHeight / this.imgNaturalHeight;
        } else {
            // 图片比屏幕高，以宽度为基准，高度会超出
            return screenWidth / this.imgNaturalWidth;
        }
    }

    /**
     * 计算目标缩放比例
     * @returns {number} 缩放比例
     */
    calculateTargetScale() {
        const targetRect = this.targetArea.getBoundingClientRect();
        const targetWidth = targetRect.width;
        const targetHeight = targetRect.height;
        const targetAspectRatio = targetWidth / targetHeight;

        let scale;

        if (this.imgAspectRatio > targetAspectRatio) {
            // 图片比目标区域宽，以高度为基准
            scale = targetHeight / this.imgNaturalHeight;
        } else {
            // 图片比目标区域高，以宽度为基准
            scale = targetWidth / this.imgNaturalWidth;
        }

        return scale;
    }

    /**
     * 计算目标位置
     * @returns {Object} x, y偏移量
     */
    calculateTargetPosition() {
        const targetRect = this.targetArea.getBoundingClientRect();
        const screenRect = this.pinnedScreen.getBoundingClientRect();

        // 计算目标区域的中心点
        const targetCenterX = targetRect.left + targetRect.width / 2;
        const targetCenterY = targetRect.top + targetRect.height / 2;

        // 计算屏幕中心点
        const screenCenterX = screenRect.left + screenRect.width / 2;
        const screenCenterY = screenRect.top + screenRect.height / 2;

        // 计算移动距离（从图片当前中心到目标区域中心）
        // 注意：图片当前已经缩放，但transform-origin是center，所以图片中心在屏幕中心
        const moveX = targetCenterX - screenCenterX;
        const moveY = targetCenterY - screenCenterY;

        // console.log(targetRect)
        // console.log(targetCenterX, targetCenterY)
        // console.log(screenCenterX, screenCenterY)
        return { x: moveX, y: moveY };
    }

    /**
     * 创建动画
     */
    createAnimation() {
        // 设置图片初始状态
        const initialScale = this.calculateInitialScale();
        gsap.set(this.mainMedia, {
            scale: initialScale,
            x: 0,
            y: 0
        });

        // 隐藏目标区域（如果需要）
        gsap.set(this.targetArea, { opacity: 0 });

        // 获取目标变换参数
        const targetScale = this.calculateTargetScale();
        const targetPosition = this.calculateTargetPosition();

        // 创建动画时间线
        if (this.mainTimeline && typeof this.mainTimeline.kill === 'function') this.mainTimeline.kill();
        this.mainTimeline = gsap.timeline({
            scrollTrigger: {
                trigger: this.pinnedScreen,
                start: "top top",
                end: `+=${this.config.scrollDistance}`,
                scrub: this.config.scrubDelay,
                pin: true,
                anticipatePin: 1,
                invalidateOnRefresh: true,
                // markers: true,
                onUpdate: (self) => {
                    this.currentProgress = self.progress;
                    
                    // 更新进度条
                    if (this.progressBar) {
                        this.progressBar.style.width = `${this.currentProgress * 100}%`;
                    }
                    
                    // 调用进度变化回调
                    if (typeof this.config.onProgressChange === 'function') {
                        this.config.onProgressChange(this.currentProgress);
                    }
                },
                onEnter: () => {
                    this.isAnimating = true;
                    
                    // // 显示目标区域
                    // gsap.to(this.targetArea, {
                    //     opacity: 1,
                    //     duration: this.config.targetFadeInDuration,
                    //     ease: this.config.targetFadeInEase
                    // });
                    
                    // 调用动画开始回调
                    if (typeof this.config.onAnimationStart === 'function') {
                        this.config.onAnimationStart();
                    }
                },
                onLeave: () => {
                    this.isAnimating = false;
                    
                    // 调用动画完成回调
                    if (typeof this.config.onAnimationComplete === 'function') {
                        this.config.onAnimationComplete();
                    }
                },
                onLeaveBack: () => {
                    // gsap.to(this.targetArea, { 
                    //     opacity: 0, 
                    //     duration: this.config.targetFadeOutDuration,
                    //     ease: this.config.targetFadeOutEase
                    // });
                }
            }
        });

        // 添加动画序列
        this.addAnimationSequence();

        if (typeof this.config.extendTimeline === 'function') {
            this.config.extendTimeline(
                this.mainTimeline,
                { meida: this.mainMedia, target: this.targetArea, screen: this.pinnedScreen },
                {
                    calculateTargetScale: () => this.calculateTargetScale(),
                    calculateTargetPosition: () => this.calculateTargetPosition()
                }
            );
        }

        // 监听窗口大小变化
        this.setupResizeListener();

        // console.log('全屏缩放动画已初始化', {
        //     initialScale,
        //     targetScale,
        //     targetPosition
        // });
    }

    /**
     * 添加动画序列
     */
    addAnimationSequence() {
        this.mainTimeline
            .to(this.mainMedia, {
                scale: () => this.calculateTargetScale(),
                x: () => this.calculateTargetPosition().x,
                y: () => this.calculateTargetPosition().y,
                duration: this.config.zoomDuration,
                ease: this.config.zoomEase,
                onComplete: () => {
                    if (typeof this.config.onZoomComplete === 'function') {
                        this.config.onZoomComplete(this.ele);
                    }
                }
            }, 0)
            
            // .to(this.mainMedia, {
            //     borderRadius: "24px",
            //     // boxShadow: "0 25px 70px rgba(0, 0, 0, 0.7)",
            //     duration: 0.5,
            //     ease: "back.out(1.7)"
            // }, 0.8);
    }

    /**
     * 设置窗口大小变化监听
     */
    setupResizeListener() {
        let resizeTimeout;
        
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                if (this.mainTimeline) {
                    const newInitialScale = this.calculateInitialScale();
                    const currentProgress = this.mainTimeline.progress();
                    
                    if (currentProgress < 0.1) {
                        gsap.set(this.mainMedia, {
                            scale: newInitialScale,
                            x: 0,
                            y: 0,
                            borderRadius: 0,
                            boxShadow: "none"
                        });
                    }
                    
                    // console.log('窗口大小改变，重新计算参数');
                }
            }, 250);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize);
        this.handleResize = handleResize;
    }

    /**
     * 重新计算动画参数
     */
    recalculate() {
        if (!this.isInitialized) return;
        
        const initialScale = this.calculateInitialScale();
        const targetScale = this.calculateTargetScale();
        const targetPosition = this.calculateTargetPosition();
        
        gsap.set(this.mainMedia, {
            scale: initialScale,
            x: 0,
            y: 0,
            borderRadius: 0,
            boxShadow: "none"
        });
        
        // console.log('动画参数已重新计算', {
        //     initialScale,
        //     targetScale,
        //     targetPosition
        // });
    }

    /**
     * 跳转到动画指定进度
     * @param {number} progress 进度值（0-1）
     */
    goToProgress(progress) {
        if (!this.mainTimeline) return;
        
        progress = Math.max(0, Math.min(1, progress));
        this.mainTimeline.progress(progress);
        this.currentProgress = progress;
        
        // 更新进度条
        if (this.progressBar) {
            this.progressBar.style.width = `${progress * 100}%`;
        }
    }

    /**
     * 播放动画
     */
    play() {
        if (!this.mainTimeline) return;
        this.mainTimeline.play();
    }

    /**
     * 暂停动画
     */
    pause() {
        if (!this.mainTimeline) return;
        this.mainTimeline.pause();
    }

    /**
     * 重置动画到开始
     */
    reset() {
        if (!this.mainTimeline) return;
        this.mainTimeline.progress(0);
        this.currentProgress = 0;
        
        if (this.progressBar) {
            this.progressBar.style.width = '0%';
        }
        
        gsap.set(this.targetArea, { opacity: 0 });
    }

    /**
     * 销毁动画实例
     */
    destroy() {
        if (this.mainTimeline) {
            this.mainTimeline.kill();
            this.mainTimeline = null;
        }
        
        // 移除进度条
        if (this.ownsProgressBar && this.progressIndicator) {
            if (this.progressIndicator.parentNode) {
                this.progressIndicator.parentNode.removeChild(this.progressIndicator);
            }
        }
        this.progressIndicator = null;
        this.progressBar = null;
        this.ownsProgressBar = false;
        
        if (this.handleResize) {
            window.removeEventListener('resize', this.handleResize);
            window.removeEventListener('scroll', this.handleResize);
            this.handleResize = null;
        }
        
        // 重置图片状态
        gsap.set(this.mainMedia, {
            scale: 1,
            x: 0,
            y: 0,
            borderRadius: 0,
            boxShadow: "none"
        });
        
        // 重置目标区域
        gsap.set(this.targetArea, { opacity: 1 });
        
        this.isInitialized = false;
        this.isAnimating = false;
        
        // console.log('全屏缩放动画已销毁');
    }

    /**
     * 获取主时间线（GSAP Timeline）
     * 可用于在初始化后继续追加滚动驱动的动画
     * @returns {gsap.core.Timeline|null} 主时间线
     */
    getTimeline() {
        return this.mainTimeline;
    }

    /**
     * 获取关键元素引用
     * @returns {{ image: HTMLElement, target: HTMLElement, screen: HTMLElement }}
     */
    getElements() {
        return { image: this.mainMedia, media: this.mainMedia, target: this.targetArea, screen: this.pinnedScreen };
    }

    /**
     * 获取当前状态
     * @returns {Object} 当前状态信息
     */
    getStatus() {
        return {
            initialized: this.isInitialized,
            animating: this.isAnimating,
            progress: this.currentProgress,
            imageProperties: {
                naturalWidth: this.imgNaturalWidth,
                naturalHeight: this.imgNaturalHeight,
                aspectRatio: this.imgAspectRatio
            }
        };
    }
}

// 默认样式（可选）
const defaultStyles = `
/* fullscreen-zoom.css - 全屏缩放动画样式 */

/* 进度指示器 */
.fsz-progress-indicator {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: rgba(255, 255, 255, 0.1);
    z-index: 1000;
}

.fsz-progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6);
    width: 0%;
    transition: width 0.1s linear;
}

/* 全屏图片容器 */
.fsz-media-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
    overflow: hidden;
}

/* 全屏图片 */
.fsz-fullscreen-media {
    max-width: unset;
    max-height: unset;
    width: auto !important; 
    height: auto !important;
    position: absolute;
    will-change: transform;
    transform-origin: center center;
}

/* 固定屏幕区域 */
.fsz-pinned-screen {
    min-height: 100vh;
    width: 100%;
    position: relative;
    overflow: hidden;
    background: #000;
}

/* 目标区域 *//* 
.fsz-target-container {
    border-radius: 24px;
    overflow: hidden;
    box-shadow: 0 25px 70px rgba(0, 0, 0, 0.7);
    z-index: 20;
    background: rgba(20, 20, 20, 0.6);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.2rem;
    font-weight: 500;
    opacity: 0;
}*/

`;

// 自动注入默认样式
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('style');
    styleElement.textContent = defaultStyles;
    document.head.appendChild(styleElement);
}

// 导出类
if (typeof module !== 'undefined' && module.exports) {
    module.exports = FullScreenZoom;
} else if (typeof define === 'function' && define.amd) {
    define([], function() {
        return FullScreenZoom;
    });
} else {
    window.FullScreenZoom = FullScreenZoom;
}
